import React, { useEffect, useState } from "react";

import { Col, Row } from "antd";
import { Form, Formik } from "formik";

import * as Yup from "yup";

import {
  useCosignProperties,
  useCreateCosignProperty,
} from "manager/hooks/api";
import { ICosignProperty } from "manager/hooks/api/integrationQueries";
import { Input, VeroFormField } from "shared/components/Form";
import { Modal } from "shared/components/Modal/Modal";

import {
  CreatePropertyError,
  CreatePropertyForm,
  DuplicateWarningBanner,
  DuplicateWarningDismiss,
  DuplicateWarningText,
} from "./styled";

export interface CreateCosignPropertyModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  veroProperty: {
    id: string;
    name: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    numberOfUnits: number;
  };
}

interface CreatePropertyFormValues {
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  numberOfUnits: number | string;
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required("Required"),
  address: Yup.string().trim().required("Required"),
  city: Yup.string().trim().required("Required"),
  state: Yup.string().trim().required("Required"),
  zipCode: Yup.string().trim().required("Required"),
  numberOfUnits: Yup.number()
    .typeError("Enter a number")
    .integer("Enter a whole number")
    .min(1, "Must be at least 1")
    .required("Required"),
});

const normalize = (value?: string) => (value || "").trim().toLowerCase();

const findDuplicate = (
  properties: ICosignProperty[] | undefined,
  name: string,
  address: string
): ICosignProperty | undefined => {
  if (!properties || properties.length === 0) {
    return undefined;
  }
  const normName = normalize(name);
  const normAddress = normalize(address);
  return properties.find(
    (property) =>
      (property.name && normalize(property.name) === normName) ||
      (property.address && normalize(property.address) === normAddress)
  );
};

const extractErrorMessage = (error: any) =>
  error?.errors?.error ||
  error?.errors?.nonFieldErrors?.[0] ||
  "Unable to create the property in Cosign. Please try again.";

const CreateCosignPropertyModal: React.FC<CreateCosignPropertyModalProps> = ({
  open,
  onClose,
  onCreated,
  veroProperty,
}) => {
  const [formError, setFormError] = useState<string | null>(null);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const { cosignProperties } = useCosignProperties({ enabled: open });
  const { createCosignProperty, isCreatingCosignProperty } =
    useCreateCosignProperty();

  // Reset transient UI state whenever the modal (re)opens for a property.
  useEffect(() => {
    if (open) {
      setFormError(null);
      setWarningDismissed(false);
      setHasSubmitted(false);
    }
  }, [open, veroProperty.id]);

  const initialValues: CreatePropertyFormValues = {
    name: veroProperty.name ?? "",
    address: veroProperty.address ?? "",
    city: veroProperty.city ?? "",
    state: veroProperty.state ?? "",
    zipCode: veroProperty.zipCode ?? "",
    numberOfUnits: veroProperty.numberOfUnits ?? "",
  };

  const handleSubmit = (values: CreatePropertyFormValues) => {
    setFormError(null);
    // Once a create is in flight, suppress the duplicate warning. On success the
    // create-property cache is invalidated and refetched (awaited by react-query
    // before onCreated runs), so the property we just created would otherwise be
    // matched as a "similar property" and flash a confusing banner before the
    // modal closes.
    setHasSubmitted(true);
    createCosignProperty(
      {
        vero_property_id: veroProperty.id,
        name: values.name.trim(),
        address: values.address.trim(),
        city: values.city.trim(),
        state: values.state.trim(),
        zip_code: values.zipCode.trim(),
        units: Number(values.numberOfUnits),
      },
      {
        onSuccess: () => {
          onCreated();
        },
        onError: (error: any) => {
          // Re-enable the duplicate warning so a corrected resubmission is guarded.
          setHasSubmitted(false);
          setFormError(extractErrorMessage(error));
        },
      }
    );
  };

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ submitForm, isValid, values }) => {
        const duplicate = findDuplicate(
          cosignProperties,
          values.name,
          values.address
        );

        return (
          <Form>
            <Modal
              visible={open}
              close={onClose}
              afterClose={() => setFormError(null)}
              title="Create Property in Cosign"
              submitButtonLabel={
                isCreatingCosignProperty || hasSubmitted
                  ? "Creating..."
                  : "Create & Map"
              }
              submit={submitForm}
              closeOnSubmit={false}
              // Stay in the in-flight state until the operation finishes (the modal
              // closes on success, or hasSubmitted resets on error). isCreatingCosignProperty
              // alone flips back to false during the awaited post-success refetch, which
              // would otherwise briefly re-enable the button and allow a duplicate submit.
              submitting={isCreatingCosignProperty || hasSubmitted}
              submitButtonDisabled={
                isCreatingCosignProperty || hasSubmitted || !isValid
              }
            >
              <Modal.Body>
                {duplicate && !warningDismissed && !hasSubmitted && (
                  <DuplicateWarningBanner data-testid="cosign-duplicate-warning">
                    <DuplicateWarningText>
                      We found a similar property in Cosign:{" "}
                      <strong>{duplicate.name || duplicate.address}</strong>
                      {duplicate.uuid ? ` (${duplicate.uuid})` : ""}. Make sure
                      you are not creating a duplicate before continuing.
                    </DuplicateWarningText>
                    <DuplicateWarningDismiss
                      type="button"
                      onClick={() => setWarningDismissed(true)}
                      data-testid="cosign-duplicate-warning-dismiss"
                    >
                      Dismiss
                    </DuplicateWarningDismiss>
                  </DuplicateWarningBanner>
                )}

                {formError && (
                  <CreatePropertyError data-testid="cosign-create-error">
                    {formError}
                  </CreatePropertyError>
                )}

                <CreatePropertyForm>
                  <Row gutter={[20, 20]}>
                    <Col span={24}>
                      <VeroFormField
                        id="name"
                        name="name"
                        data-testid="name"
                        label="Name"
                        required
                        as={Input}
                      />
                    </Col>
                  </Row>
                  <Row gutter={[20, 20]}>
                    <Col span={24}>
                      <VeroFormField
                        id="address"
                        name="address"
                        data-testid="address"
                        label="Address"
                        required
                        as={Input}
                      />
                    </Col>
                  </Row>
                  <Row gutter={[20, 20]}>
                    <Col md={{ span: 12 }} xs={{ span: 24 }}>
                      <VeroFormField
                        id="city"
                        name="city"
                        data-testid="city"
                        label="City"
                        required
                        as={Input}
                      />
                    </Col>
                    <Col md={{ span: 12 }} xs={{ span: 24 }}>
                      <VeroFormField
                        id="state"
                        name="state"
                        data-testid="state"
                        label="State"
                        required
                        as={Input}
                      />
                    </Col>
                  </Row>
                  <Row gutter={[20, 20]}>
                    <Col md={{ span: 12 }} xs={{ span: 24 }}>
                      <VeroFormField
                        id="zipCode"
                        name="zipCode"
                        data-testid="zipCode"
                        label="Zip Code"
                        required
                        as={Input}
                      />
                    </Col>
                    <Col md={{ span: 12 }} xs={{ span: 24 }}>
                      <VeroFormField
                        id="numberOfUnits"
                        name="numberOfUnits"
                        data-testid="numberOfUnits"
                        label="Number of Units"
                        type="number"
                        required
                        as={Input}
                      />
                    </Col>
                  </Row>
                </CreatePropertyForm>
              </Modal.Body>
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreateCosignPropertyModal;
